import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  TextField,
  FormControl,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {IFdLiability, IGenericType} from 'constants/interfaces';

export type Submission = {
  id?: string;
  creditLimitAmount: number;
  currentBalanceLimit: number;
  monthlyInstallmentAmount: number;
  inArrears: boolean;
  liabilityAccountTypeId: number;
  lender: string;
};

export function FdLiabilityForm(props: {
  liability?: IFdLiability;
  allLiabilities: IGenericType[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (submission: Submission) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  readonly?: boolean;
}) {
  const {handleSubmit, control} = useForm<Submission>({
    defaultValues: {
      id: props.liability?.id,
      creditLimitAmount: props.liability?.creditLimitAmount,
      currentBalanceLimit: props.liability?.currentBalanceLimit,
      monthlyInstallmentAmount: props.liability?.monthlyInstallmentAmount,
      inArrears: props.liability?.inArrears || false,
      liabilityAccountTypeId: props.liability?.liabilityAccountType?.id,
      lender: props.liability?.lender || '',
    },
  });

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      ...submission,
    });
  };

  return (
    <div className="generic-form-container fd-liability-form">
      <div className="form-container" style={{width: '100%'}}>
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Liability Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="liabilityAccountTypeId"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <FormControl fullWidth error={!!error}>
                    <Autocomplete
                      options={props.allLiabilities}
                      getOptionLabel={(option: IGenericType) => option.name!}
                      value={
                        value
                          ? props.allLiabilities.find((l) => l.id == value)
                          : null
                      }
                      onChange={(_, newValue) => onChange(newValue?.id || null)}
                      disabled={props.readonly || props.isLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Liability Type"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          helperText={error ? error.message : null}
                        />
                      )}
                    />
                  </FormControl>
                )}
                rules={{required: 'Liability Type is required'}}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="lender"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Lender"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Lender is required'}}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="creditLimitAmount"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Credit Limit Amount"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Credit limit amount is required'}}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="currentBalanceLimit"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Current Balance Limit"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Current balance limit is required'}}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="monthlyInstallmentAmount"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    label="Monthly Installment Amount"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{required: 'Monthly installment amount is required'}}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="inArrears"
                control={control}
                render={({field: {onChange, value}}) => (
                  <FormControl>
                    <Typography variant="subtitle2" gutterBottom>
                      In Arrears
                    </Typography>
                    <Button
                      variant={value ? 'contained' : 'outlined'}
                      color={value ? 'primary' : 'default'}
                      onClick={() => onChange(!value)}
                      disabled={props.readonly || props.isLoading}
                    >
                      {value ? 'Yes' : 'No'}
                    </Button>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className="secondary-btn"
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className="primary-btn"
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress color="primary" size={24} thickness={6} />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
